import { CheckCircle2, ChevronDown, ExternalLink, Globe2, Mic, Trophy, Users } from "lucide-react";
import { LOGO } from "@/lib/links";

const chip = "inline-flex items-center gap-1 rounded-md bg-[var(--luma-bg)] px-2 py-0.5 text-[11px] font-bold text-[var(--luma-muted)]";

function More({ t, lum }) {
  return (
    <div id={`theory-more-${t.id}`} data-testid={`theory-full-${t.id}`} className="fade-up space-y-3 pt-1 text-base leading-[1.7] text-[var(--luma-ink)]">
      {t.approfondimento && <p className="whitespace-pre-line">{t.approfondimento}</p>}
      {t.indizi?.length > 0 && (
        <ul className="space-y-1 text-[15px]">
          {t.indizi.map((i, k) => <li key={k}>• {i.testo}{i.capitolo && <b className="ml-1">(cap. {i.capitolo})</b>}</li>)}
        </ul>
      )}
      {lum?.nota && <p className="whitespace-pre-line rounded-xl bg-[var(--luma-soft-purple)] p-4 text-[15px]">{lum.nota}</p>}
      {lum?.links?.map((l, k) => (
        <a key={k} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`lumacofono-link-${t.id}-${k}`} className="mr-4 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-[var(--luma-action)] hover:underline">
          <Mic size={14} /> {l.titolo || "Ascolta / guarda"}
        </a>
      ))}
    </div>
  );
}

function Chips({ t, lum, mondo, comm, mine, choice, winner }) {
  if (!lum && !mondo && !comm && !mine && !choice && !winner) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-1.5">
      {lum && <span className={chip} data-testid={`badge-lumacofono-${t.id}`}><img src={LOGO} alt="" className="h-4 w-4 rounded-full" /> Teoria di Lumacofono</span>}
      {mondo && <span className={chip}><Globe2 size={11} /> Dal mondo</span>}
      {comm && <span className={chip}><Users size={11} /> Community</span>}
      {choice && <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-[var(--luma-pink)]" data-testid={`badge-choice-${t.id}`}><Mic size={12} /> La scelta di Lumacofono</span>}
      {winner && <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-700"><Trophy size={12} /> Rivelata nel manga</span>}
    </div>
  );
}

export function TheoryCard({ t, idx, picked, mine, choice, winner, locked, expanded, onToggle, onPick }) {
  const lum = t.attribuzioni.find((a) => a.tipo === "lumacofono");
  const fonti = t.attribuzioni.filter((a) => a.tipo === "mondo");
  const comm = t.attribuzioni.filter((a) => a.tipo === "community");
  const hasMore = !!t.approfondimento || !!lum?.nota || (lum?.links?.length || 0) > 0 || (t.indizi?.length || 0) > 0;
  const on = picked || mine;
  return (
    <article data-testid={`option-card-${t.id}`} aria-labelledby={`theory-title-${t.id}`}
      className={`rounded-[18px] border bg-white p-[22px] shadow-[0_3px_12px_rgba(20,35,61,.04)] [overflow-wrap:anywhere] sm:p-7 ${on ? "border-[var(--luma-action)] bg-[#FCFAFF] ring-1 ring-[var(--luma-action)]" : "border-[var(--luma-line)]"}`}
      style={{ transition: "border-color .18s, background-color .18s" }}>
      <Chips t={t} lum={lum} mondo={fonti.length > 0} comm={comm.length > 0} mine={mine} choice={choice} winner={winner} />
      <h3 id={`theory-title-${t.id}`} className="text-xl font-extrabold leading-tight sm:text-2xl">{t.titolo}</h3>
      {t.riassunto && <p data-testid={`theory-summary-${t.id}`} className="mt-3 text-base leading-[1.65] text-[var(--luma-muted)] sm:text-[17px]">{t.riassunto}</p>}
      {expanded && <More t={t} lum={lum} />}
      {hasMore && (
        <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls={`theory-more-${t.id}`} data-testid={`option-expand-${t.id}`}
          className="-ml-1 mt-1 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-1 text-sm font-bold text-[var(--luma-action)] hover:underline">
          {expanded ? "Riduci la teoria" : "Leggi tutta la teoria"} <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
      {fonti.map(({ fonte: f }) => (
        <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`source-link-${f.id}`} className="flex min-h-[36px] items-center gap-1.5 text-xs font-bold text-[#0E7F96] hover:underline">
          <ExternalLink size={13} /> Fonte originale: {f.piattaforma || "link"}{f.data_pubblicazione && ` · ${new Date(f.data_pubblicazione).toLocaleDateString("it-IT", { month: "short", year: "numeric" })}`}
        </a>
      ))}
      {comm.length > 0 && <p className="mt-1 text-xs text-[var(--luma-muted)]">Proposta da: {comm.map((c) => c.autore_nome).join(", ")}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[#ECF1F6] pt-4">
        <span className="text-xs font-semibold tracking-[0.08em] text-[var(--luma-muted)]">TEORIA {String(idx).padStart(2, "0")}</span>
        {!locked && (
          <button type="button" onClick={onPick} aria-pressed={picked} data-testid={`option-pick-${t.id}`} className="luma-pick">
            {picked ? "Selezionata ✓" : "Scelgo questa teoria"}
          </button>
        )}
        {mine && <span data-testid={`option-mine-${t.id}`} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-[var(--luma-action)]"><CheckCircle2 size={16} /> La tua scelta</span>}
      </div>
    </article>
  );
}
